import {
  Box,
  Button,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from 'constants/routes.constants';
import { useFooter } from 'queries/global/useFooter';

import AppStores from 'components/@common/AppStores';
import Contact from 'components/@common/Contact';
import LanguagePickerButton from 'components/@common/LanguagePickerButton';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import LogoNofomo from 'components/@navigation/LogoNofomo';
import Socials from 'components/@navigation/Socials';

const Footer = () => {
  const { footerLinks } = useFooter();
  const intl = useIntl();

  return (
    <Container component="footer">
      <Stack
        sx={{
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 5,
          py: 5,
        }}
      >
        <Box
          aria-label={intl.formatMessage({
            id: 'navigation.home',
            defaultMessage: 'Home',
          })}
          component={LinkUnstyled}
          href={Routes.Home}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            pl: { xs: 0 },
          }}
        >
          <LogoNofomo width={96} height={40} color="global01.400" />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            width: '100%',
            gap: { xs: 5, md: 2 },
            py: { xs: 0, md: 5 },
          }}
        >
          <Stack>
            <Typography
              variant="overline"
              sx={{
                mb: 2,
              }}
            >
              <FormattedMessage id="footer.section.app_stores" />
            </Typography>
            <AppStores sx={{ flexDirection: { xs: 'row', md: 'column' } }} />
          </Stack>

          <Divider
            sx={{
              borderColor: 'grey.800',
              display: { xs: 'block', md: 'none' },
            }}
          />

          <Stack>
            <Typography
              variant="overline"
              sx={{
                mb: 2,
              }}
            >
              <FormattedMessage id="footer.section.contact" />
            </Typography>
            <Contact />
          </Stack>

          <Stack>
            <Typography
              variant="overline"
              sx={{
                mb: 2,
              }}
            >
              <FormattedMessage id="footer.section.privacy" />
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: { xs: 2, md: 3 },
              }}
            >
              <Button
                variant="text"
                component={LinkUnstyled}
                href={Routes.PrivacyPolicy}
              >
                <FormattedMessage id="account.settings.privacy" />
              </Button>
              <Button
                variant="text"
                component={LinkUnstyled}
                href={Routes.TermsOfService}
                sx={{ justifyContent: { xs: 'flex-start' } }}
              >
                <FormattedMessage id="account.settings.terms.of.use" />
              </Button>
            </Box>
          </Stack>

          <Stack>
            <Typography
              variant="overline"
              sx={{
                mb: 2,
              }}
            >
              <FormattedMessage id="footer.section.links" />
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: { xs: 2, md: 3 },
              }}
            >
              {footerLinks?.map((link) => (
                <Button
                  key={link.id}
                  variant="text"
                  component={LinkUnstyled}
                  target="_blank"
                  href={link.url}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Stack>

          <Divider
            sx={{
              borderColor: 'grey.800',
              display: { xs: 'block', md: 'none' },
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            width: '100%',
          }}
        >
          <LanguagePickerButton />
          <Socials />
        </Box>
      </Stack>
    </Container>
  );
};

export default Footer;
